import {
  loginCodeStatusSchema,
  requestLoginCodeResponseSchema,
  tenantAuthMeSchema,
  type LoginCodeStatus,
  type RequestLoginCodeResponse,
  type TenantAuthMe,
} from '@console/types'
import { z } from 'zod'
import { ApiError } from '../api.js'
import { customerAppGet, customerAppPost } from '../customer-app.js'

/**
 * `null` means "not signed in" — same convention `auth/api.ts`'s `fetchMe`
 * uses for the operator session, kept identical so `RequireTenantAuth` can
 * mirror `RequireAuth`'s own three-state handling exactly.
 */
export async function fetchTenantMe(): Promise<TenantAuthMe | null> {
  try {
    return await customerAppGet('/api/v1/tenant-auth/me', tenantAuthMeSchema)
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null
    throw error
  }
}

export async function requestTenantOtp(phone: string): Promise<void> {
  await customerAppPost('/api/v1/tenant-auth/request-otp', { phone }, z.object({ ok: z.boolean() }))
}

export async function verifyTenantOtp(phone: string, code: string): Promise<TenantAuthMe> {
  return customerAppPost('/api/v1/tenant-auth/verify-otp', { phone, code }, tenantAuthMeSchema)
}

export async function tenantLogout(): Promise<void> {
  await customerAppPost('/api/v1/tenant-auth/logout', undefined, z.object({ ok: z.boolean() }))
}

/** `LGH-050`, `PLT-106`. Always returns a real code — see `requestLoginCodeResponseSchema`'s own comment. */
export async function requestLoginCode(phone: string): Promise<RequestLoginCodeResponse> {
  return customerAppPost(
    '/api/v1/tenant-auth/request-login-code',
    { phone },
    requestLoginCodeResponseSchema,
  )
}

/**
 * A poll AFTER the first `confirmed` response, or a bogus code, answers
 * `404` — `1C`'s own instruction is to treat that identically to `expired`
 * for UI purposes (offer a fresh code), so it is mapped here rather than
 * left for every caller to remember, same shape `fetchTenantMe`'s
 * null-on-401 convention already uses for the same reason.
 */
export async function pollLoginCode(code: string): Promise<LoginCodeStatus> {
  try {
    return await customerAppGet(
      `/api/v1/tenant-auth/login-code/${encodeURIComponent(code)}`,
      loginCodeStatusSchema,
    )
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return { status: 'expired' as const }
    throw error
  }
}
